import { calculateAu13, au13RulesFor } from "../au13/index.js";
import {
  type BalancingDirection,
  type CcsBufferInput,
  CcsBufferInputSchema,
  type CcsBufferOutput,
  type CcsBufferRawInput,
  type CcsBufferRuleSet,
} from "./types.js";

/** Round to whole cents — the convention for a dollar projection. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Total CCS subsidy per fortnight across all of the family's care lines at a
 * given family income, computed by the SHIPPED au13 engine (never re-derived).
 */
function subsidyPerFortnightAt(
  input: CcsBufferInput,
  income: number,
  rules: CcsBufferRuleSet,
): number {
  const au13Rules = au13RulesFor(rules.au13Fy);
  let total = 0;
  for (const line of input.careLines) {
    total += calculateAu13({ ...line, familyIncome: income }, au13Rules)
      .subsidyPerFortnight;
  }
  return round2(total);
}

/**
 * The withholding % that exactly neutralises a gross OVERPAYMENT — i.e. the
 * fraction `w` of the annual subsidy paid such that `w × annualPaid == gross`.
 * Returns `null` when there is no overpayment to neutralise, or when no subsidy
 * was paid to withhold against. Clamped to the legal 0..1 range (a debt larger
 * than 100% of the subsidy paid caps at 1, leaving a residual).
 */
function neutralisingRate(
  grossOverpayment: number,
  annualSubsidyPaid: number,
): number | null {
  if (grossOverpayment <= 0) return null;
  if (annualSubsidyPaid <= 0) return null;
  const raw = grossOverpayment / annualSubsidyPaid;
  // 4dp keeps it a usable withholding %; clamp to the legal 0..1 lever range.
  const clamped = Math.min(1, Math.max(0, raw));
  return Math.round(clamped * 10_000) / 10_000;
}

/**
 * Hard panel ship-condition: NO financial-product / affiliate / paid handoff
 * identifier may reach a personalised result surface (information-only,
 * no-handoff). Walks a payload and throws if any forbidden key is present.
 * Encoded as code, not a comment.
 */
const FORBIDDEN_HANDOFF_KEYS = [
  "affiliate",
  "affiliateUrl",
  "affiliateId",
  "referralUrl",
  "productHandoff",
  "financialProduct",
  "ctaUrl",
  "sponsorUrl",
  "partnerUrl",
];

export function assertNoProductHandoff(payload: unknown): void {
  if (payload === null || typeof payload !== "object") return;
  const seen = new WeakSet<object>();
  const walk = (node: unknown): void => {
    if (node === null || typeof node !== "object") return;
    if (seen.has(node as object)) return;
    seen.add(node as object);
    for (const [key, value] of Object.entries(node)) {
      if (FORBIDDEN_HANDOFF_KEYS.includes(key)) {
        throw new Error(
          `ccs-buffer: forbidden financial-product/affiliate handoff "${key}" ` +
            `cannot reach a personalised result surface (information-only, no handoff)`,
        );
      }
      walk(value);
    }
  };
  walk(payload);
}

/**
 * Project the CCS balancing position and the withholding-buffer optimisation.
 *
 * Pure: same inputs + ruleset always yield the same output. Validates at the
 * boundary. The output is the user's OWN modelled estimate — Services Australia
 * determines the final amount (the framing is enforced in copy.ts).
 *
 * Method (all figures from the au13 engine + the FY withholding constants):
 *   - subsidy paid through the year = subsidy at the LODGED estimate;
 *   - subsidy entitled = subsidy at the fresher PROJECTED income;
 *   - gross balancing = annual paid − annual entitled (+ = overpaid → debt);
 *   - the chosen withholding buffer (rate × annual paid) offsets a debt; a top-up
 *     releases the buffer on top;
 *   - the neutralising withholding % is the rate that zeroes a residual debt.
 */
export function calculateCcsBuffer(
  rawInput: CcsBufferRawInput,
  rules: CcsBufferRuleSet,
): CcsBufferOutput {
  const input: CcsBufferInput = CcsBufferInputSchema.parse(rawInput);

  const subsidyPerFortnightAtEstimate = subsidyPerFortnightAt(
    input,
    input.lodgedIncomeEstimate,
    rules,
  );
  const subsidyPerFortnightAtProjection = subsidyPerFortnightAt(
    input,
    input.projectedActualIncome,
    rules,
  );

  const annualSubsidyPaid = round2(
    subsidyPerFortnightAtEstimate * input.fortnightsInCare,
  );
  const annualSubsidyEntitled = round2(
    subsidyPerFortnightAtProjection * input.fortnightsInCare,
  );

  // Positive = the family was paid more than it was entitled to → overpaid.
  const grossBalancingAmount = round2(annualSubsidyPaid - annualSubsidyEntitled);

  // The buffer held over the year = withholding rate × subsidy PAID (withholding
  // is applied to the subsidy actually paid on the estimate).
  const withheldBuffer = round2(
    input.currentWithholdingRate * annualSubsidyPaid,
  );

  let projectedBalancingAmount: number;
  let direction: BalancingDirection;
  let bufferReleased: number;

  if (grossBalancingAmount > 0) {
    // Overpaid: the buffer offsets the overpayment; any remainder is a debt.
    const residual = round2(grossBalancingAmount - withheldBuffer);
    if (residual > 0) {
      projectedBalancingAmount = residual;
      direction = "debt";
      bufferReleased = 0;
    } else {
      // Buffer fully covered the overpayment; the surplus buffer is released.
      projectedBalancingAmount = residual; // ≤ 0 → released to the family
      direction = residual < 0 ? "top_up" : "balanced";
      bufferReleased = round2(-residual);
    }
  } else if (grossBalancingAmount < 0) {
    // Underpaid: top-up of the shortfall PLUS the withheld buffer released.
    projectedBalancingAmount = round2(grossBalancingAmount - withheldBuffer);
    direction = "top_up";
    bufferReleased = withheldBuffer;
  } else {
    // Exactly balanced on the gross figure; the whole buffer is released.
    projectedBalancingAmount = round2(-withheldBuffer);
    direction = withheldBuffer > 0 ? "top_up" : "balanced";
    bufferReleased = withheldBuffer;
  }

  const recovery =
    direction === "debt" && projectedBalancingAmount > 0
      ? {
          recoveryRate: rules.debtRecoveryRate,
          debtAmount: projectedBalancingAmount,
        }
      : null;

  const output: CcsBufferOutput = {
    subsidyPerFortnightAtEstimate,
    subsidyPerFortnightAtProjection,
    annualSubsidyPaid,
    annualSubsidyEntitled,
    grossBalancingAmount,
    withheldBuffer,
    projectedBalancingAmount,
    direction,
    bufferReleased,
    recovery,
    neutralisingWithholdingRate: neutralisingRate(
      grossBalancingAmount,
      annualSubsidyPaid,
    ),
  };

  // Guard: nothing financial-product / affiliate-shaped may ride out on the
  // surface (information-only, no handoff — panel ship-condition).
  assertNoProductHandoff(output);
  return output;
}
